// =============================================
// App.jsx - 메인 앱 컴포넌트 (화면 전체 구조)
// =============================================
// React에서 컴포넌트란 화면의 한 조각이야.
// App.jsx는 모든 페이지를 연결해주는 최상위 컴포넌트야.
// 페이지 이동(라우팅)은 react-router-dom이 담당해.
//
// ★ 로그인 기능 추가 후 달라진 점:
//   - AuthProvider로 전체 앱을 감싸서 로그인 상태를 전역 공유
//   - /login 라우트 추가
//   - /portfolio 는 ProtectedRoute로 보호 (비로그인 → /login 이동)
//   - 네비게이션 바에 로그인 상태 + 로그아웃 버튼 추가
// =============================================

import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import HomePage      from './pages/HomePage';
import KimchiPage    from './pages/KimchiPage';
import PortfolioPage from './pages/PortfolioPage';
import LoginPage     from './pages/LoginPage';
import TickerBar     from './components/TickerBar';
import { AuthProvider, useAuth } from './contexts/AuthContext';


// =============================================
// ProtectedRoute — 로그인 필수 페이지 보호
// =============================================
// 로그인하지 않은 상태에서 /portfolio에 접근하면
// 자동으로 /login 페이지로 이동시켜.
//
// 사용법:
//   <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
function ProtectedRoute({ children }) {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    // replace: true → 뒤로가기를 눌렀을 때 /portfolio로 돌아가지 않게 해
    return <Navigate to="/login" replace />;
  }

  return children;
}


// =============================================
// NavLink — 현재 페이지 강조 링크
// =============================================
function NavLink({ to, children }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link to={to} style={{
      ...styles.link,
      color:         isActive ? '#63b3ed' : 'white',
      borderBottom:  isActive ? '2px solid #63b3ed' : '2px solid transparent',
      paddingBottom: '2px',
    }}>
      {children}
    </Link>
  );
}


// =============================================
// NavBar — 상단 네비게이션 바
// =============================================
// 로그인 상태에 따라 다른 UI를 보여줘:
//   - 로그인 중 : 사용자 이름(로그인방식 아이콘) + 로그아웃 버튼
//   - 비로그인  : 파란 "로그인" 버튼
function NavBar() {
  const { isLoggedIn, user, logout } = useAuth();

  return (
    <nav style={styles.nav}>
      {/* 좌측: 로고 */}
      <Link to="/" style={{ textDecoration: 'none' }}>
        <h1 style={styles.logo}>💹 돈텔미</h1>
      </Link>

      {/* 우측: 메뉴 + 로그인 상태 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
        <NavLink to="/">홈</NavLink>
        <NavLink to="/portfolio">내 자산</NavLink>
        <NavLink to="/kimchi">김치프리미엄</NavLink>

        {isLoggedIn ? (
          // ─ 로그인 중 ─
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {/* 로그인 방식 아이콘 */}
            <span style={{ fontSize: '0.75rem' }}>
              {user?.provider === 'google'   ? '🟢' :
               user?.provider === 'telegram' ? '🔵' : '●'}
            </span>
            {/* 사용자 이름 */}
            <span style={{
              color:        '#a0aec0',
              fontSize:     '0.85rem',
              maxWidth:     '120px',
              overflow:     'hidden',
              textOverflow: 'ellipsis',
              whiteSpace:   'nowrap',
            }}>
              {user?.name || '사용자'}
            </span>
            {/* 로그아웃 버튼 */}
            <button
              onClick={logout}
              style={{
                padding:         '0.3rem 0.8rem',
                backgroundColor: 'transparent',
                border:          '1px solid #4a5568',
                borderRadius:    '6px',
                color:           '#718096',
                cursor:          'pointer',
                fontSize:        '0.78rem',
              }}
            >
              로그아웃
            </button>
          </div>
        ) : (
          // ─ 비로그인 ─
          <Link
            to="/login"
            style={{
              padding:         '0.35rem 1rem',
              backgroundColor: '#3182ce',
              color:           'white',
              borderRadius:    '7px',
              textDecoration:  'none',
              fontSize:        '0.85rem',
              fontWeight:      600,
            }}
          >
            로그인
          </Link>
        )}
      </div>
    </nav>
  );
}


// =============================================
// App — 최상위 컴포넌트
// =============================================
function App() {
  return (
    // ① BrowserRouter: URL 변화를 감지해서 페이지를 바꿔줘
    <BrowserRouter>
      {/* ② AuthProvider: 로그인 상태를 앱 전체에서 공유 */}
      <AuthProvider>
        <div style={styles.app}>

          {/* 실시간 티커 바 (최상단 스크롤 지수 표시) */}
          <TickerBar />

          {/* 상단 네비게이션 바 (로그인 상태 포함) */}
          <NavBar />

          {/* 페이지 본문 */}
          <main style={styles.main}>
            <Routes>
              {/* 공개 페이지 — 로그인 없이 접근 가능 */}
              <Route path="/"       element={<HomePage />}   />
              <Route path="/kimchi" element={<KimchiPage />} />
              <Route path="/login"  element={<LoginPage />}  />

              {/* 보호된 페이지 — 로그인 필수 */}
              {/* 비로그인 상태에서 /portfolio 접근 시 /login으로 자동 이동 */}
              <Route
                path="/portfolio"
                element={
                  <ProtectedRoute>
                    <PortfolioPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>

          {/* 하단 푸터 */}
          <footer style={styles.footer}>
            <p>돈텔미 (Don-Tell-Me) · 주식/코인 정보 & 텔레그램 알림 서비스</p>
          </footer>

        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}


// =============================================
// 인라인 스타일
// =============================================
const styles = {
  app: {
    minHeight:       '100vh',
    display:         'flex',
    flexDirection:   'column',
    fontFamily:      'sans-serif',
    backgroundColor: '#171923',
  },
  nav: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         '0 2rem',
    backgroundColor: '#1a202c',
    color:           'white',
    height:          '60px',
  },
  logo: {
    margin:   0,
    fontSize: '1.2rem',
    color:    'white',
  },
  link: {
    color:          'white',
    textDecoration: 'none',
    fontSize:       '1rem',
  },
  main: {
    flex:      1,
    padding:   '2rem',
    maxWidth:  '1200px',
    margin:    '0 auto',
    width:     '100%',
    boxSizing: 'border-box',
  },
  footer: {
    textAlign:       'center',
    padding:         '1rem',
    backgroundColor: '#1a202c',
    color:           '#a0aec0',
    fontSize:        '0.85rem',
  },
};

export default App;
